import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Clarity from '@microsoft/clarity';
import { capturePosthogEvent } from '../../../utils/posthog';
import { HiOutlinePlus, HiOutlineUserGroup } from 'react-icons/hi';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { CgImage } from 'react-icons/cg';
import { FiDownload } from 'react-icons/fi';
import { MdOutlinePhotoCamera } from 'react-icons/md';
import { LuCopy } from 'react-icons/lu';
import { RiDeleteBinLine } from 'react-icons/ri';
import { BsLayoutSidebarReverse } from 'react-icons/bs';
import { Tooltip } from 'flowbite-react';
import { editorStore } from '../../../store/editorStore';
import { floorplannerStore } from '../../../store/floorplannerStore';
import { renderStore } from '../../../store/renderStore';
import { userStore } from '../../../store/userStore';
import { canShareTeam } from '../../../utils/canShareTeam';
import ConfirmDeletePopup from '../ConfirmDeletePopup';
import ShareFloorplanModal from '../ShareFloorplanModal';
import { DisplayLengthUnitToggle } from '../../DisplayLengthUnitToggle';
import type { PropertyPanelController } from './types';
import { observer } from 'mobx-react-lite';

type PropertyPanelHeaderProps = Pick<
  PropertyPanelController,
  'optionMenuOpen' | 'setOptionMenuOpen' | 'optionMenuRef' | 'floorplans' | 'isDeletePopupOpen' | 'setIsDeletePopupOpen'
>;

/** Shared chrome for the small icon buttons in the narrow property-panel header. */
const iconButtonClass =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-stone-400/70 bg-white text-zinc-900 hover:bg-gray-100 cursor-pointer";

const PropertyPanelHeader: React.FC<PropertyPanelHeaderProps> = observer(({
  optionMenuOpen,
  setOptionMenuOpen,
  optionMenuRef,
  floorplans,
  isDeletePopupOpen,
  setIsDeletePopupOpen,
}) => {
  const { t } = useTranslation();
  const [rendering, setRendering] = useState(false);
  const [infoPopupOpen, setInfoPopupOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const infoPopupRef = useRef<HTMLDivElement>(null);
  const ownedFloorplanCount = floorplans.filter((fp) => fp.isOwner !== false).length;
  const showShare =
    canShareTeam() &&
    floorplannerStore.isOwner &&
    Boolean(floorplannerStore.id);

  useEffect(() => {
    if (!infoPopupOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        infoPopupRef.current &&
        !infoPopupRef.current.contains(event.target as Node)
      ) {
        setInfoPopupOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [infoPopupOpen]);

  return (
    <div
      className={`flex w-full min-w-0 items-center gap-1.5 select-none ${
        editorStore.propertyPanelMinimized ? 'justify-end p-2' : 'px-2 pt-2 pb-2'
      }`}
    >
      {!editorStore.propertyPanelMinimized && (
        <>
          {/* Compact unit chip — segmented mm|in was too wide for this 226px panel */}
          <DisplayLengthUnitToggle
            variant="compact"
            className="!h-8 !w-8 shrink-0 !rounded-md !border-stone-400/70"
          />

          <div
            className={`relative ${iconButtonClass} ${optionMenuOpen ? '!bg-black text-white hover:!bg-black' : ''}`}
            onClick={() => {
              setInfoPopupOpen(false);
              setOptionMenuOpen(!optionMenuOpen);
            }}
            ref={optionMenuRef}
            aria-label={t('propertyPanel.moreOptions', { defaultValue: 'More options' })}
          >
            <div className="pb-0.5 text-center text-lg font-normal leading-none font-['DM Sans']">···</div>
            {optionMenuOpen && (
              <div className="fixed right-20 top-16 w-auto bg-black text-white border border-gray-300 shadow-lg rounded-lg py-2 z-20">
                <div
                  className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOptionMenuOpen(false);
                    setInfoPopupOpen(true);
                  }}
                >
                  <IoMdInformationCircleOutline className="text-xl transform scale-90" />
                  {t('propertyPanel.information')}
                </div>
                <div className="border-b border-[#66645F]" />
                <div
                  className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                  onClick={() => {
                    if (userStore.isFreemiumUser) {
                      setOptionMenuOpen(false);
                      editorStore.showPaywall('addToProject');
                      return;
                    }
                    editorStore.clearSelections();
                    editorStore.openAddToPresentationPopup();
                  }}
                >
                  <CgImage className="text-xl transform scale-90" />
                  {t('propertyPanel.addToProject')}
                </div>
                <div className="border-b border-[#66645F]" />
                {showShare && (
                  <>
                    <div
                      className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                      onClick={() => {
                        setOptionMenuOpen(false);
                        setShareModalOpen(true);
                      }}
                    >
                      <HiOutlineUserGroup className="text-xl transform scale-90" />
                      {t('propertyPanel.shareWithTeam')}
                    </div>
                    <div className="border-b border-[#66645F]" />
                  </>
                )}
                {editorStore.view3D && (
                  <>
                    <div
                      className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                      onClick={() => {
                        editorStore.openRender3DViewModal();
                        setOptionMenuOpen(false);
                      }}
                    >
                      <MdOutlinePhotoCamera className="text-xl transform scale-90" />
                      {t('propertyPanel.renderPhoto')}
                    </div>
                    <div className="border-b border-[#66645F]" />
                  </>
                )}
                <div
                  className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                  onClick={() => {
                    if (userStore.isFreemiumUser) {
                      setOptionMenuOpen(false);
                      editorStore.showPaywall('download');
                      return;
                    }
                    editorStore.clearSelections();
                    editorStore.openDownloadProjectPopup();
                  }}
                >
                  <FiDownload className="text-xl transform scale-90" />
                  {t('propertyPanel.downloadProject')}
                </div>
                <div className="border-b border-[#66645F]" />
                <div
                  className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                  onClick={() => {
                    const limit = userStore.maxFloorplans;
                    if (limit != null && ownedFloorplanCount >= limit) {
                      setOptionMenuOpen(false);
                      editorStore.showPaywall('project');
                      return;
                    }
                    Clarity.event('new_project');
                    capturePosthogEvent('new_project');
                    window.location.href = '/floorplan/';
                  }}
                >
                  <HiOutlinePlus className="text-xl transform scale-90" />
                  {t('propertyPanel.createNewProject')}
                </div>
                <div
                  className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                  onClick={() => {
                    const limit = userStore.maxFloorplans;
                    if (limit != null && ownedFloorplanCount >= limit) {
                      setOptionMenuOpen(false);
                      editorStore.showPaywall('project');
                      return;
                    }
                    Clarity.event('duplicate_project');
                    capturePosthogEvent('duplicate_project');
                    floorplannerStore.duplicateProject();
                  }}
                >
                  <LuCopy className="text-xl transform scale-90" />
                  {t('propertyPanel.copyProject')}
                </div>
                {floorplannerStore.isOwner && (
                  <div
                    className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                    onClick={() => {
                      setIsDeletePopupOpen(true);
                      setOptionMenuOpen(false);
                    }}
                  >
                    <RiDeleteBinLine className="text-xl transform scale-90" />
                    {t('propertyPanel.deleteProject')}
                  </div>
                )}
              </div>
            )}
          </div>

          {infoPopupOpen && (
            <div
              ref={infoPopupRef}
              className="fixed right-20 top-16 w-80 bg-white border border-gray-300 shadow-lg rounded-lg py-2 z-50"
            >
              <div className="px-4 py-2">
                <h3 className="font-semibold text-sm mb-2">{t('propertyPanel.title')}</h3>
                <p className="text-xs text-gray-600 mb-2">{t('propertyPanel.infoDescription')}</p>
                <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                  <li>{t('propertyPanel.wallDimensions')}</li>
                  <li>{t('propertyPanel.symbolPropertiesList')}</li>
                  <li>{t('propertyPanel.lineColorsWeightsStyles')}</li>
                  <li>{t('propertyPanel.fillColors')}</li>
                </ul>
                <p className="text-xs text-gray-600 mt-2">{t('propertyPanel.emptyHint')}</p>
              </div>
            </div>
          )}

          {/* Primary action — gets remaining width so "Save" / "Saved" is readable */}
          <div
            className={
              'flex h-8 min-w-0 flex-1 items-center justify-center rounded-md px-2 cursor-pointer' +
              (floorplannerStore.dirty && !floorplannerStore.lockedByOther
                ? ' bg-boniYellowPrimary text-blackplum'
                : ' bg-eggshell text-gray-900') +
              (floorplannerStore.lockedByOther ? ' opacity-50 cursor-not-allowed' : '')
            }
            onClick={() => {
              if (floorplannerStore.lockedByOther) return;
              setRendering(true);
              if (editorStore.camera) {
                if (editorStore.view3D) {
                  renderStore.capture3DSnapshot('upload', undefined, editorStore.camera);
                } else {
                  renderStore.captureSnapshot('upload', undefined, undefined, editorStore.camera);
                }
              }
              floorplannerStore.saveToCloud();
              setTimeout(() => {
                setRendering(false);
              }, 200);
            }}
          >
            {rendering ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
            ) : (
              <span className="truncate font-['DM Sans'] text-center text-sm font-medium">
                {floorplannerStore.lockedByOther
                  ? t('propertyPanel.locked')
                  : floorplannerStore.dirty
                    ? t('propertyPanel.save')
                    : t('propertyPanel.saved')}
              </span>
            )}
          </div>

          {isDeletePopupOpen && (
            <ConfirmDeletePopup
              onConfirm={() => {
                floorplannerStore.deleteFromCloud(floorplannerStore.id);
                setIsDeletePopupOpen(false);
              }}
              onCancel={() => setIsDeletePopupOpen(false)}
            />
          )}
          {shareModalOpen && (
            <ShareFloorplanModal
              show={shareModalOpen}
              onClose={() => setShareModalOpen(false)}
            />
          )}
        </>
      )}
      <Tooltip
        content={
          editorStore.propertyPanelMinimized
            ? t('common.maximize')
            : t('common.minimize')
        }
        placement="bottom"
      >
        <div
          className={iconButtonClass}
          onClick={() =>
            editorStore.setPropertyPanelMinimized(!editorStore.propertyPanelMinimized)
          }
          aria-label={
            editorStore.propertyPanelMinimized
              ? t('common.maximize')
              : t('common.minimize')
          }
        >
          <div
            className={`flex h-5 w-5 items-center justify-center rounded-sm ${
              editorStore.propertyPanelMinimized ? 'bg-eggshell300' : ''
            }`}
          >
            <BsLayoutSidebarReverse style={{ strokeWidth: 0.5 }} />
          </div>
        </div>
      </Tooltip>
    </div>
  );
});

export default PropertyPanelHeader;
